import {
  APPEAL_RETROSPECTIVE,
  CONTINUED_STAY,
  EMERGENCY_DEPARTMENT,
  ESCALATE_FOR_PEER_TO_PEER,
  INITIAL,
} from '../PatientWorkQueue/PatientWorkQueueTable.constants';
import type { CustomPopulationEncounterKeys } from './CustomPopulation.types';
import { formatClsNumberFilter } from './utils/formatClsNumberFilter';
import { formatEscalatedPatientsFilter } from './utils/formatEscalatedPatientsFilter';
import { formatLOSFilter } from './utils/formatLOSFilter';
import { formatSimpleFilter } from './utils/formatSimpleFilter';
import { formatStringInRangeFilter } from './utils/formatStringInRangeFilter';

export const CUSTOM_POPULATION = 'Custom Population';
export const CUSTOM_POPULATIONS = 'Custom Populations';
export const ADD_SMART_FILTERS = '+ Add Smart Filters';
export const EXPORT_LIST = 'Export List';
export const RESET_COLUMNS = 'Reset Columns';
export const CURRENTLY_VIEWING = 'Currently viewing:';
export const SMART_FILTER_PREFIX = 'SMART_FILTER_';
export const SMART_FILTERS = 'Smart Filters';
export const MAX_SMART_FILTERS = 3;
export const REVENUE_COMPLIANCE_IMPACT = 'Revenue Compliance Impact';
export const READMISSION = 'Readmission';

export const MAX_LENGTH_ROWS_FOR_EXPORT = 1500;
export const CUSTOM_POPULATION_CSV_DOWNLOAD_SUCESS_MESSAGE =
  'Custom Population exported successfully';
export const CUSTOM_POPULATION_CSV_DOWNLOAD_FAIL_MESSAGE =
  ' Failed to export CSV';
export const CUSTOM_POPULATION_WORKQUEUE_CONFIRMATION_MESSAGE =
  'You are attempting to export a list containing over 1500 rows of data.\nTo maintain optimal system performance, we have set a limit of 1500 rows for each export operation.\nPlease select "Ok" to proceed with exporting only the top 1500 rows of your selected data.\nPlease select  "Cancel" to return to the work queue and refine your data selection, we kindly suggest applying filters to export smaller segments of your data.\n';
export const CUSTOM_POPULATION_WORKQUEUE_CONFIRMATION_TITTLE =
  'Attention: Data Export Limit Warning';

export enum CustomPopulationColumnHeaders {
  ACCOUNT_NUMBER = 'Account #',
  ASSIGNMENT = 'Assignment',
  ATTENDING_PHYSICIAN = 'Attending Physician',
  CHIEF_COMPLAINT = 'Chief Complaint',
  CURRENT_CLS = 'Current CLS',
  DOB = 'DOB',
  FACILITY = 'Facility',
  HOSPITAL_SERVICE = 'Hospital Service',
  LAST_PA_REVIEW_DATE_TIME = 'Last PA Review Date/Time',
  LOCATION = 'Location',
  MAX_CLS = 'Max CLS',
  MEL = 'MeL',
  MRN = 'MRN',
  PA_REVIEW_BY = 'PA Review By',
  PA_REVIEW_STATUS = 'PA Review Status',
  PA_REVIEW_TYPE = 'PA Review Type',
  PATIENT_NAME = 'Patient Name',
  PATIENT_STATUS = 'Patient Status',
  PRESENTATION_DATE = 'Presentation Date',
  PRIMARY_PAYER_TYPE = 'Primary Payer Type',
  PRIMARY_PLAN_NAME = 'Primary Plan Name',
  ROOM_NUMBER = 'Room #',
  TOTAL_LENGTH_OF_STAY = 'Total LOS',
}

export const CustomPopulationColumnField: Record<
  string,
  CustomPopulationEncounterKeys
> = {
  ATTENDING_PHYSICIAN: 'attendingPhysician',
  BIRTH_DATE: 'birthDate',
  CHIEF_COMPLAINT: 'chiefComplaint',
  CLIENT_FACILITY_NAME: 'clientFacilityName',
  CLS: 'cls',
  FACILITY_PHYSICIAN_ADVISOR_EMAIL: 'facilityPhysicianAdvisorEmail',
  FACILITY_PHYSICIAN_ADVISOR_ID: 'facilityPhysicianAdvisorId',
  FULL_NAME: 'fullName',
  HOSPITAL_SERVICE: 'hospitalService',
  IS_ASSIGNED_TO_XSOLIS_INTERNAL_PA: 'isAssignedToXsolisInternalPa',
  LAST_COMPLETED_REVIEW_DATE: 'lastCompletedReviewDateTimeZoned',
  LAST_COMPLETED_REVIEW_USER: 'lastCompletedReviewUser',
  LAST_NAME: 'lastName',
  LOCATION: 'location',
  MAX_CLS: 'maxCls',
  MEDICAL_RECORD_NUMBER: 'medicalRecordNumber',
  MEL: 'mel',
  PATIENT_ACCOUNT_NUMBER: 'patientAccountNumber',
  PEDIATRIC_INDICATOR: 'pediatricIndicator',
  PRESENTATION_DATE: 'presentationDateTimeZoned',
  PRIMARY_INSURANCE_PLAN: 'primaryInsurancePlan',
  PRIMARY_PAYER_TYPE: 'primaryPayerType',
  REVIEW_STATUS: 'reviewStatus',
  REVIEW_TYPE_NAME: 'reviewTypeName',
  ROOM: 'room',
  STATUS: 'status',
  TLOS: 'tlos',
  TOTAL_LENGTH_OF_STAY: 'totalLengthOfStay',
} as const;

export enum EscalatedPatientsSelection {
  ASSIGNED = 'Assigned - Pending Review Completion',
  ASSIGNED_ESCALATIONS = 'Assigned Escalations - Pending Review Creation',
  COMPLETED = 'Completed Escalations',
  UNASSIGNED = 'Unassigned - Outstanding Escalations',
}

export enum CustomPopulationFilterKeys {
  ATTENDING_PHYSICIAN = 'attendingPhysician',
  CLIENT_FACILITY_ID = 'clientFacilityId',
  CLS = 'Cls',
  ESCALATED_PATIENTS_SELECTION = 'escalatedPatientsSelection',
  HOSPITAL_SERVICE = 'hospitalService',
  LOCATIONS = 'locations',
  LOS = 'los',
  MAX_CLS = 'MaxCls',
  MEL = 'Mel',
  PAYER_NAME_ID = 'payerNameId',
  PRIMARY_PAYER_TYPE = 'primaryPayerType',
  STATUS = 'status',
}

export enum LOSTypeValues {
  DAYS = 'days',
  HOURS = 'hours',
}

export const reviewTypesForEscalations = [
  CONTINUED_STAY,
  INITIAL,
  APPEAL_RETROSPECTIVE,
  ESCALATE_FOR_PEER_TO_PEER,
  EMERGENCY_DEPARTMENT,
];

export const populationFilterFieldsMap = {
  [CustomPopulationFilterKeys.ATTENDING_PHYSICIAN]: {
    formatter: formatSimpleFilter,
    populationFields: 'physicians',
  },
  [CustomPopulationFilterKeys.CLIENT_FACILITY_ID]: {
    formatter: formatStringInRangeFilter,
    populationFields: 'facilities',
  },
  [CustomPopulationFilterKeys.CLS]: {
    formatter: formatClsNumberFilter,
    populationFields: 'currentCLS',
  },
  [CustomPopulationFilterKeys.ESCALATED_PATIENTS_SELECTION]: {
    formatter: formatEscalatedPatientsFilter,
    populationFields: 'escalatedPatientsSelection',
  },
  [CustomPopulationFilterKeys.HOSPITAL_SERVICE]: {
    formatter: formatSimpleFilter,
    populationFields: 'hospitalServices',
  },
  [CustomPopulationFilterKeys.LOCATIONS]: {
    formatter: formatSimpleFilter,
    populationFields: 'locations',
  },
  [CustomPopulationFilterKeys.LOS]: {
    formatter: formatLOSFilter,
    populationFields: [
      'los',
      'losRangeType',
      'losType',
      'losRangeStart',
      'losRangeEnd',
    ],
  },
  [CustomPopulationFilterKeys.MAX_CLS]: {
    formatter: formatClsNumberFilter,
    populationFields: 'maxCLS',
  },
  [CustomPopulationFilterKeys.MEL]: {
    formatter: formatStringInRangeFilter,
    populationFields: 'meLPrediction',
  },
  [CustomPopulationFilterKeys.PAYER_NAME_ID]: {
    formatter: formatStringInRangeFilter,
    populationFields: 'primaryPayerNames',
  },
  [CustomPopulationFilterKeys.PRIMARY_PAYER_TYPE]: {
    formatter: formatStringInRangeFilter,
    populationFields: 'primaryPayerTypes',
  },
  [CustomPopulationFilterKeys.STATUS]: {
    formatter: formatStringInRangeFilter,
    populationFields: 'patientStatuses',
  },
};

export enum PatientStatus {
  DISCHARGED_AS_ED = 'Discharged as ED',
  DISCHARGED_AS_INPT = 'Discharged as INPT',
  DISCHARGED_AS_OBS = 'Discharged as OBS',
  DISCHARGED_AS_OUTPT = 'Discharged as OUTPT',
  EMERGENCY_ROOM = 'Emergency_Room',
  INPATIENT = 'Inpatient',
  OBSERVATION = 'Observation',
  OUTPATIENT = 'Outpatient',
  PREADMIT = 'Preadmit',
}

export enum PAReviewType {
  APPEAL_RETROSPECTIVE = 'Appeal/Retrospective PA',
  BLANK = '',
  CONTINUED_STAY_PAY = 'Continued Stay PA',
  INITIAL_PA = 'Initial PA',
  PEER_TO_PEER_PA = 'Peer-to-Peer PA',
  PEER_TO_PEER_XSOLIS_PA = 'Peer-To-Peer XSOLIS PA',
}

export enum PAReviewStatus {
  BLANK = '',
  COMPLETED = 'Completed',
  IN_PROGRESS = 'In Progress',
  PA_TO_SCHEDULE = 'PA To Schedule',
  PENDING = 'Pending',
  SCHEDULED = 'Scheduled',
}

export const FILTERS_STATE = 'filters-state';
export const SMART_FILTERS_STATE = 'smart-filters-state';
